// ──────────────────────────────────────────────────────────────────────────
// Guest → account migration.
//
// Called once after a successful sign-in OR sign-up. Safe to re-run (uses
// upsert with ignoreDuplicates on habits/goals; checkins are unique by
// habit_id + date). Does NOT clear the local guest blob unless the migration
// completes end-to-end.
//
// Contract:
//   - If there's nothing to migrate, returns { migrated: false }.
//   - If this user already ran migration on this device, returns { migrated: false }.
//   - On success: clears the guest blob and sets the migrated flag.
//   - On failure: leaves the guest blob intact; caller shows a retry banner.
// ──────────────────────────────────────────────────────────────────────────

import { supabase } from './supabase.js';
import { guestStore } from './guest.js';
import { v4 as uuidv4 } from 'uuid';

export async function migrateGuestData(userId) {
  if (!userId) return { migrated: false, reason: 'no-user' };
  if (guestStore._migratedFor() === userId) return { migrated: false, reason: 'already-migrated' };

  const blob = guestStore._readRaw();
  const hasAny =
    (blob.habits?.length || 0) +
    (blob.checkins?.length || 0) +
    (blob.goals?.length || 0) +
    Object.keys(blob.briefings || {}).length > 0 ||
    !!blob.profile?.displayName;

  // Intelligence Brief keys live outside the guest blob (they're managed
  // by briefStore, not guestStore), so we migrate them ahead of the
  // early-exit in case the user has guest briefs but nothing else to
  // move server-side. Safe to run every time because the dedup-by-id
  // inside mergeBriefsById is idempotent — but we still guard on
  // `_migratedFor` up top so this only runs once per (userId, device).
  try {
    mergeGuestKeyInto('proxera.briefs.v1',     userId, mergeBriefsById);
    mergeGuestKeyInto('proxera.briefPrefs.v1', userId, (_existing, incoming) => incoming);
  } catch (err) {
    console.warn('[migration] brief-key copy failed (non-fatal)', err);
  }

  if (!hasAny) {
    guestStore._markMigrated(userId);
    return { migrated: false, reason: 'empty' };
  }

  // Build an old->new ID map in case we need to remap anything. In practice
  // client UUIDs are already unique, so we use them as-is; but if a user
  // somehow already has a habit with the same id server-side, upsert's
  // ignoreDuplicates will keep the server's.
  const habitIdMap = new Map();

  // 1. Habits
  if (blob.habits?.length) {
    const rows = blob.habits.map(h => {
      const serverId = h.id && isUuid(h.id) ? h.id : uuidv4();
      habitIdMap.set(h.id, serverId);
      return {
        id: serverId,
        user_id: userId,
        name: h.name,
        icon: h.icon,
        accent: h.accent,
        frequency: h.frequency || 'Daily',
        custom_days: h.custom_days || null,
        sort_order: h.sort_order ?? 0,
        end_year: h.end_year ?? null,
        end_month: h.end_month ?? null,
        streak: h.streak ?? 0,
        longest: h.longest ?? 0,
      };
    });
    const { error } = await supabase.from('habits').upsert(rows, {
      onConflict: 'id',
      ignoreDuplicates: true,
    });
    if (error) throw error;
  }

  // 2. Checkins — remap habit_id through habitIdMap
  if (blob.checkins?.length) {
    const rows = blob.checkins
      .map(c => ({
        user_id: userId,
        habit_id: habitIdMap.get(c.habit_id) || c.habit_id,
        date: c.date,
        status: c.status || 'done',
      }))
      .filter(r => isUuid(r.habit_id));
    if (rows.length) {
      const { error } = await supabase.from('checkins').upsert(rows, {
        onConflict: 'habit_id,date',
        ignoreDuplicates: true,
      });
      if (error) throw error;
    }
  }

  // 3. Goals
  // Local guest blobs store the UI's 'monthly'/'yearly' strings. The server
  // CHECK constraint only accepts 'month'/'year', so normalize or the whole
  // migration aborts on the first goal row.
  if (blob.goals?.length) {
    const rows = blob.goals.map(g => ({
      id: g.id && isUuid(g.id) ? g.id : uuidv4(),
      user_id: userId,
      kind: g.kind === 'monthly' ? 'month' : g.kind === 'yearly' ? 'year' : g.kind,
      period: g.period,
      text: g.text,
      checked: !!g.checked,
      sort_order: g.sort_order ?? 0,
    }));
    const { error } = await supabase.from('goals').upsert(rows, {
      onConflict: 'id',
      ignoreDuplicates: true,
    });
    if (error) throw error;
  }

  // 4. Briefings (tasks + meta per date)
  if (blob.briefings && Object.keys(blob.briefings).length) {
    for (const [date, b] of Object.entries(blob.briefings)) {
      const taskRows = (b.tasks || []).map((t, i) => ({
        id: t.id && isUuid(t.id) ? t.id : uuidv4(),
        user_id: userId,
        date,
        text: t.text,
        checked: !!t.checked,
        priority: (t.priority || 'MED').toUpperCase(),
        sort_order: i,
      }));
      if (taskRows.length) {
        const { error } = await supabase.from('briefing_tasks').upsert(taskRows, {
          onConflict: 'id',
          ignoreDuplicates: true,
        });
        if (error) throw error;
      }
      const { error: metaErr } = await supabase.from('briefing_meta').upsert(
        {
          user_id: userId,
          date,
          focus: b.focus || null,
          calls: b.calls || [],
          date_display: b.date_display || null,
        },
        { onConflict: 'user_id,date', ignoreDuplicates: true }
      );
      if (metaErr) throw metaErr;
    }
  }

  // 5. Display name
  if (blob.profile?.displayName) {
    const { error } = await supabase
      .from('profiles')
      .update({ display_name: blob.profile.displayName })
      .eq('id', userId);
    if (error) throw error;
  }

  // Intelligence Brief keys were migrated up top, before the early-exit.

  // All good — clear guest blob
  guestStore._clear();
  guestStore._markMigrated(userId);
  return { migrated: true };
}

// Copy a guest-scoped localStorage key into the user-scoped one, merging
// via the provided `merge(existing, incoming)` function. `existing` is
// what's already under the user's key (often nothing); `incoming` is the
// guest blob. If neither side has data, the user key is left untouched.
function mergeGuestKeyInto(prefix, userId, merge) {
  const guestKey = `${prefix}:guest`;
  const userKey  = `${prefix}:${userId}`;
  const rawGuest = safeReadJSON(guestKey);
  const rawUser  = safeReadJSON(userKey);
  if (rawGuest == null) return;
  const merged = merge(rawUser, rawGuest);
  if (merged == null) return;
  try {
    localStorage.setItem(userKey, JSON.stringify(merged));
  } catch {}
}

function safeReadJSON(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

// Merge two brief arrays by brief.id. If the same id appears in both,
// prefer the existing (user-bucket) copy — the user may have edited or
// regenerated audio server-side, and we don't want the stale guest copy
// to clobber that. Sort newest-first so the merged array matches the
// order listBriefs() would produce.
function mergeBriefsById(existing, incoming) {
  const ex = Array.isArray(existing) ? existing : [];
  const inc = Array.isArray(incoming) ? incoming : [];
  const byId = new Map();
  for (const b of inc) { if (b && b.id) byId.set(b.id, b); }
  // Existing wins on conflict.
  for (const b of ex)  { if (b && b.id) byId.set(b.id, b); }
  const merged = Array.from(byId.values());
  merged.sort((a, b) => String(b.generatedAt || '').localeCompare(String(a.generatedAt || '')));
  return merged;
}

function isUuid(s) {
  return typeof s === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s);
}
